import * as React from 'react';
import  { withStyles } from '@mui/styles';

import {WithStyles, StyleRules} from '@mui/styles';
import {PredefinedField} from '../lib/types';
import {Theme} from '@mui/styles';
import {Typography, TextField, Button, IconButton} from '@mui/material';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';

const styles = (theme: Theme): StyleRules => ({
  container: {
    width: '100%'
  },
  sectionTitle: {
    fontSize: '1rem'
  },
  instructions: {
    padding: 15
  },
  formArea: {
    padding: 15
  },
  path: {
    padding: 2,
    width: '20%'
  },
  operator: {
    padding: 2,
    width: '10%'
  },
  value: {
    padding: 2,
    width: '20%'
  },
  attributeName: {
    padding: 2,
    width: '20%'
  },
  attributeValue: {
    padding: 2,
    width: '20%'
  },
  fieldRow: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between'
  },
  actionBar: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  itemDelete: {
    fontSize: '0.80rem',
    width: 45,
    height: 45
  }
});

interface Props extends React.HTMLProps<HTMLDivElement> {
  field: PredefinedField;
  onConditionalAttributesChange: (newConditionalAttributes: Array<any>) => void;
}

// We will kepp internal state to give more conditionalAttributes to the consumers, but we will always defer to the data coming from the props.
type State = {
  conditionalAttributes: any;
};

class ConditionalAttributesView extends React.Component<
  Props & WithStyles<keyof typeof styles>,
  State
> {
  constructor(props: Props & WithStyles<keyof typeof styles>) {
    super(props, {});
    this.state = {
      conditionalAttributes: this.props.field.conditionalAttributes || []
    };
  }

  UNSAFE_componentWillReceiveProps(
    newProps: Props & WithStyles<keyof typeof styles>,
    oldProps: Props & WithStyles<keyof typeof styles>
  ) {
    // When we get new props, always defer to those over internal state.  This is crucial to the predictable operation of the component parts
    // Initiate loading here is any loading is required.  Do not forget to also implement incomponentDidMount also
    this.setState({
      conditionalAttributes: newProps.field.conditionalAttributes || []
    });
  }

  handleAttributeChange = (idx: number) => (attribute: string) => (
    evt: any
  ) => {
    const {conditionalAttributes} = this.state;
    conditionalAttributes[idx][attribute] = evt.target.value;
    this.setState({conditionalAttributes});
    if (this.props.onConditionalAttributesChange) {
      this.props.onConditionalAttributesChange(conditionalAttributes);
    }
  };

  handleItemDelete = (idx: number) => (evt: any) => {
    const {conditionalAttributes} = this.state;
    var newConditionalAttributes: Array<any> = conditionalAttributes.filter(
      function(value, arrIndex) {
        return idx !== arrIndex;
      }
    );
    this.setState({conditionalAttributes: newConditionalAttributes});
    if (this.props.onConditionalAttributesChange) {
      this.props.onConditionalAttributesChange(newConditionalAttributes);
    }
  };

  handleItemAdd = (evt: any) => {
    const {conditionalAttributes} = this.state;
    conditionalAttributes.push({});
    this.setState({conditionalAttributes});
    if (this.props.onConditionalAttributesChange) {
      // console.log('yo');
      this.props.onConditionalAttributesChange(conditionalAttributes);
    }
  };

  render() {
    const {classes}: {classes: any} = this.props;
    const {conditionalAttributes} = this.state;

    let conditionalAttributesArea: any = null;
    if (conditionalAttributes && conditionalAttributes.length > 0) {
      conditionalAttributesArea = conditionalAttributes.map(
        (conditionalAttribute: any, idx: any) => {
          return (
            <div key={idx} className={classes.fieldRow}>
              <TextField
                className={classes.path}
                id="path"
                name="path"
                value={conditionalAttribute.path || ''}
                label="path"
                onChange={this.handleAttributeChange(idx)('path')}
                InputLabelProps={{
                  shrink: true
                }}
              />

              <FormControl className={classes.operator}>
                <InputLabel shrink={true} required={true} htmlFor="select">
                  Operator
                </InputLabel>
                <Select
                  name="select"
                  value={conditionalAttribute.operator || ''}
                  onChange={this.handleAttributeChange(idx)('operator')}>
                  <MenuItem value="==">{'=='}</MenuItem>
                  <MenuItem value="!=">{'!='}</MenuItem>
                  <MenuItem value=">">{'>'}</MenuItem>
                  <MenuItem value=">=">{'>='}</MenuItem>
                  <MenuItem value="<">{'<'}</MenuItem>
                  <MenuItem value="<=">{'<='}</MenuItem>
                  <MenuItem value="is">{'is'}</MenuItem>
                </Select>
              </FormControl>

              <TextField
                className={classes.value}
                id="value"
                name="value"
                value={conditionalAttribute.value || ''}
                label="value"
                onChange={this.handleAttributeChange(idx)('value')}
                InputLabelProps={{
                  shrink: true
                }}
              />

              <FormControl className={classes.attributeName}>
                <InputLabel shrink={true} required={true} htmlFor="selectAttr">
                  Attribute
                </InputLabel>
                <Select
                  name="selectAttr"
                  value={conditionalAttribute.attributeName || ''}
                  onChange={this.handleAttributeChange(idx)('attributeName')}>
                  <MenuItem value="mode">Mode</MenuItem>
                  <MenuItem value="label">Label</MenuItem>
                  <MenuItem value="helpText">Help Text</MenuItem>
                  <MenuItem value="placeholder">Placeholder</MenuItem>
                  <MenuItem value="regex">Regex</MenuItem>
                  <MenuItem value="options">Options</MenuItem>
                  <MenuItem value="minLength">Min Length</MenuItem>
                  <MenuItem value="maxLength">Max Length</MenuItem>
                  <MenuItem value="minValue">Min Value</MenuItem>
                  <MenuItem value="maxValue">Max Value</MenuItem>
                </Select>
              </FormControl>

              <TextField
                className={classes.attributeValue}
                id="attributeValue"
                name="attributeValue"
                value={conditionalAttribute.attributeValue || ''}
                label="attributeValue"
                onChange={this.handleAttributeChange(idx)('attributeValue')}
                InputLabelProps={{
                  shrink: true
                }}
              />
              <IconButton
                size="large"
                aria-label="delete"
                className={classes.itemDelete}
                onClick={this.handleItemDelete(idx)}>
                <i className="fa fas fa-trash" />
              </IconButton>
            </div>
          );
        }
      );
    } else {
      conditionalAttributesArea = (
        <Typography variant="body1">
          No conditional attributes have been specified
        </Typography>
      );
    }

    return (
      <div className={classes.container}>
        <Typography className={classes.subheading} variant="subtitle1">
          Conditional Attributes
        </Typography>
        {conditionalAttributesArea}
        <div className={classes.actionBar}>
          <Button
            color="primary"
            className={classes.button}
            onClick={this.handleItemAdd}>
            Add
          </Button>
        </div>
      </div>
    );
  }
}

export default withStyles(styles)(ConditionalAttributesView);
